import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, CircleNotch, X, PencilSimple, SquaresFour } from "@phosphor-icons/react";
import { Textarea } from "@/components/ui/textarea";
import { AppHeader } from "@/components/AppHeader";
import { MicHero } from "@/components/MicHero";
import { VoiceButton } from "@/components/VoiceButton";
import { ToolChips, EnginePopover } from "@/components/Controls";
import { CompareView } from "@/components/CompareView";
import { FollowUps } from "@/components/FollowUps";
import { OutputTerminal } from "@/components/OutputTerminal";
import { MetricsPanel } from "@/components/MetricsPanel";
import { LibraryDrawer } from "@/components/LibraryDrawer";
import { TemplatesGallery } from "@/components/TemplatesGallery";
import { MobileTabBar } from "@/components/MobileTabBar";
import { getConfig, generate, streamGenerate, saveLibraryApi, addVersionApi, clarifyApi, resendVerificationApi, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MODES = [["generate", "Speak an idea"], ["refine", "Improve a prompt"], ["compare", "Compare AIs"]];
const EXAMPLES = ["A landing page for my yoga studio", "A Python script that renames my photos by date", "A cinematic image of Tokyo in the rain", "A weekly meal plan for a vegetarian runner"];

function useModelSync(config, prov, model, setModel) {
  useEffect(() => {
    const l = config.providers?.[prov]?.models || [];
    if (l.length && !l.find((m) => m.id === model)) setModel(l[0].id);
  }, [prov, config]); // eslint-disable-line
}

export default function PromptGenerator() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const outRef = useRef(null);
  const fuRef = useRef(null);
  const [config, setConfig] = useState({ providers: {}, target_tools: [] });
  const [mode, setMode] = useState("generate");
  const [input, setInput] = useState("");
  const [targetTool, setTargetTool] = useState("chatgpt");
  const [provider, setProvider] = useState("openai");
  const [model, setModel] = useState("gpt-5.4");
  const [provA, setProvA] = useState("openai");
  const [modelA, setModelA] = useState("gpt-5.4");
  const [provB, setProvB] = useState("anthropic");
  const [modelB, setModelB] = useState("claude-sonnet-4-6");
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [result, setResult] = useState(null);
  const [cmpLoading, setCmpLoading] = useState(false);
  const [cmp, setCmp] = useState(null);
  const [libOpen, setLibOpen] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [libRefresh, setLibRefresh] = useState(0);
  const [loadedItem, setLoadedItem] = useState(null);
  const [micState, setMicState] = useState("idle");
  const [followups, setFollowups] = useState(null);
  const [clarifying, setClarifying] = useState(false);
  const [clarifiedFor, setClarifiedFor] = useState("");
  const hasAccess = user?.access;

  useEffect(() => { getConfig().then(setConfig).catch(() => toast.error("Could not load configuration")); }, []);
  useModelSync(config, provider, model, setModel);
  useModelSync(config, provA, modelA, setModelA);
  useModelSync(config, provB, modelB, setModelB);

  const targetLabel = config.target_tools?.find((t) => t.id === targetTool)?.label || targetTool;
  const guardAccess = () => {
    if (hasAccess) return true;
    toast.error("Your free trial has ended. Upgrade to keep generating.");
    navigate("/pricing");
    return false;
  };

  const useTemplate = (t) => {
    setMode("generate");
    setInput(t.idea);
    setTargetTool(t.target_tool);
    setClarifiedFor(t.idea.trim());
    setFollowups(null);
    setResult(null);
    setLoadedItem(null);
    toast.success(`“${t.title}” is ready. Fill in the [brackets], then create.`);
  };

  const handleGenerate = async () => {
    if (!input.trim()) return toast.error(mode === "refine" ? "Paste a prompt to improve" : "Tap the mic or type your idea first");
    if (!guardAccess()) return;
    if (mode === "generate" && clarifiedFor !== input.trim()) {
      setClarifying(true);
      try {
        const r = await clarifyApi(input, targetTool);
        setClarifiedFor(input.trim());
        if (r.vague) {
          setFollowups(r.questions);
          setTimeout(() => fuRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 120);
          return;
        }
      } catch { /* fall through to generate */ } finally { setClarifying(false); }
    }
    runGenerate(input);
  };

  const answerFollowups = (extra) => {
    const text = input.trim() + extra;
    setFollowups(null);
    setInput(text);
    setClarifiedFor(text.trim());
    runGenerate(text);
  };

  const runGenerate = (text) => {
    setFollowups(null);
    setResult(null);
    setStreamText("");
    setStreaming(true);
    setTimeout(() => outRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    streamGenerate({ mode, input_text: text, target_tool: targetTool, provider, model }, {
      onDelta: (c) => setStreamText((p) => p + c),
      onDone: (res) => { setResult(res); setStreaming(false); },
      onError: (msg, code) => { toast.error(msg || "Generation failed"); setStreaming(false); if (code === 402) navigate("/pricing"); },
    });
  };

  const handleCompare = async () => {
    if (!input.trim()) return toast.error("Describe your goal first");
    if (!guardAccess()) return;
    setCmp(null);
    setCmpLoading(true);
    try {
      const base = { mode: "generate", input_text: input, target_tool: targetTool };
      const [a, b] = await Promise.all([generate({ ...base, provider: provA, model: modelA }), generate({ ...base, provider: provB, model: modelB })]);
      setCmp({ a, b });
    } catch (e) {
      if (e.response?.status === 402) navigate("/pricing");
      toast.error(e.response?.data?.detail || "Comparison failed");
    } finally { setCmpLoading(false); }
  };

  const doSave = async (res) => {
    try {
      if (loadedItem) { await addVersionApi(loadedItem.id, res); toast.success("Saved as a new version"); }
      else { setLoadedItem(await saveLibraryApi(res)); toast.success("Saved to your library"); }
      setLibRefresh((k) => k + 1);
    } catch { toast.error("Could not save"); }
  };

  const doShare = (res) => {
    navigator.clipboard.writeText(`${window.location.origin}/p/${res.id}`);
    toast.success("Share link copied");
  };

  const loadLibraryItem = (item) => {
    const active = item.versions[item.active_version] || item.versions[0];
    setLoadedItem(item);
    setInput(item.input_text || "");
    setTargetTool(item.target_tool || "chatgpt");
    if (active?.provider) setProvider(active.provider);
    if (active?.model) setModel(active.model);
    setMode(item.mode === "refine" ? "refine" : "generate");
    setResult({ ...active, id: null });
    toast.success("Loaded. Generate again to save a new version.");
  };

  const s = { input, setInput, targetTool, setTargetTool, provA, setProvA, modelA, setModelA, provB, setProvB, modelB, setModelB, cmp, cmpLoading, hasAccess };
  const displayText = streaming ? streamText : result?.prompt || "";
  const showOutput = streaming || result;

  return (
    <div className="stp-bg">
      <AppHeader onLibrary={() => setLibOpen(true)} />
      <main className="mx-auto w-full max-w-4xl px-5 pb-32 pt-10 sm:px-8">
        {!hasAccess && (
          <div data-testid="trial-ended-banner" className="mb-8 flex flex-col items-start justify-between gap-3 rounded-3xl bg-[#FFECEE] p-5 sm:flex-row sm:items-center">
            <p className="text-sm text-ink">Your free trial has ended. Upgrade to keep creating prompts.</p>
            <button onClick={() => navigate("/pricing")} className="h-9 rounded-full bg-ink px-4 text-xs font-semibold text-white">View plans</button>
          </div>
        )}

        {user && !user.email_verified && (
          <div data-testid="verify-email-banner" className="mb-8 flex flex-col items-start justify-between gap-3 rounded-3xl bg-brand-soft p-5 sm:flex-row sm:items-center">
            <p className="text-sm text-ink">Please verify your email. We sent a link to <strong>{user.email}</strong>.</p>
            <button data-testid="resend-verification-button" onClick={() => resendVerificationApi().then(() => toast.success("Verification email sent")).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))} className="h-9 shrink-0 rounded-full bg-ink px-4 text-xs font-semibold text-white">Resend link</button>
          </div>
        )}

        <div className="mb-10 flex justify-center">
          <div className="inline-flex rounded-full bg-black/5 p-1" data-testid="mode-switch">
            {MODES.map(([id, label]) => (
              <button key={id} data-testid={`tab-${id}`} onClick={() => { setMode(id); setFollowups(null); }} className={`whitespace-nowrap rounded-full px-3 py-2 text-[12px] font-medium transition-colors sm:px-5 sm:text-[13px] ${mode === id ? "bg-white text-ink shadow-sm" : "text-mist hover:text-ink"}`}>{label}</button>
            ))}
          </div>
        </div>

        {mode === "compare" ? (
          <CompareView s={s} config={config} onCompare={handleCompare} onShare={doShare} onSave={doSave} />
        ) : (
          <motion.section key={mode} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
            <h1 className="text-center font-display text-4xl font-semibold leading-[1.05] text-ink sm:text-5xl lg:text-6xl">
              {mode === "generate" ? "What do you want to build?" : "Make any prompt better."}
            </h1>
            <p className="mx-auto mt-4 max-w-xl text-center text-base text-mist sm:text-lg">
              {mode === "generate" ? "Just say it out loud. We'll turn your words into a ready-to-paste prompt for any AI." : "Paste a prompt you already use. We'll make it clear, structured and tuned for your AI tool."}
            </p>

            {mode === "generate" && (
              <div className="mt-8">
                <MicHero onTranscript={(t) => setInput((p) => (p ? p + " " + t : t))} disabled={streaming || !hasAccess} onStateChange={setMicState} />
              </div>
            )}

            <div className="stp-card mt-8 rounded-3xl p-5 sm:p-6" data-testid="config-panel">
              {loadedItem && (
                <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-[#FFF4E5] px-3 py-1 text-xs text-[#C93400]" data-testid="editing-chip">
                  <PencilSimple size={12} weight="bold" /> Editing a saved prompt · new results save as versions
                  <button onClick={() => setLoadedItem(null)} data-testid="editing-chip-close"><X size={12} weight="bold" /></button>
                </div>
              )}
              <div className="mb-2 flex items-center justify-between">
                <label className="text-sm font-semibold text-ink">{mode === "generate" ? (input ? "Here's what I heard. Edit anything." : "Or type it") : "Your current prompt"}</label>
                {mode === "refine" && <VoiceButton onResult={(t) => setInput((p) => (p ? p + " " + t : t))} disabled={streaming || !hasAccess} />}
                {mode === "generate" && (
                  <div className="flex items-center gap-3">
                    <button data-testid="open-templates-button" onClick={() => setTplOpen(true)} className="flex items-center gap-1 rounded-full bg-brand-soft px-3 py-1 text-xs font-semibold text-brand transition-colors hover:bg-brand/15"><SquaresFour size={13} weight="bold" /> Templates</button>
                    {input && <button data-testid="clear-input-button" onClick={() => setInput("")} className="text-xs text-mist hover:text-ink">Clear</button>}
                  </div>
                )}
              </div>
              <Textarea data-testid="input-textarea" value={input} onChange={(e) => setInput(e.target.value)} placeholder={mode === "generate" ? "e.g. A landing page for my yoga studio that gets people to book a first class" : "e.g. write me some marketing text for my app"} className="min-h-[110px] resize-none rounded-2xl border-0 bg-[#FAFAFC] text-[15px] leading-relaxed text-ink placeholder:text-mist/70 focus-visible:ring-2 focus-visible:ring-brand/30" />
              {mode === "generate" && !input && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="example-ideas">
                  {EXAMPLES.map((e, i) => (
                    <button key={e} data-testid={`example-idea-${i}`} onClick={() => setInput(e)} className="rounded-full bg-brand-soft px-3 py-1.5 text-xs font-medium text-brand hover:bg-brand/15">{e}</button>
                  ))}
                </div>
              )}
              <div className="mt-6">
                <p className="mb-2 text-xs font-medium text-mist">Made for</p>
                <ToolChips tools={config.target_tools || []} value={targetTool} onChange={setTargetTool} />
              </div>
              <div className="mt-6 flex flex-col-reverse items-stretch justify-between gap-3 sm:flex-row sm:items-center">
                <EnginePopover providers={config.providers} provider={provider} model={model} onProvider={setProvider} onModel={setModel} />
                <button data-testid="generate-button" onClick={handleGenerate} disabled={streaming || clarifying || micState !== "idle"} className="flex h-12 items-center justify-center gap-2 rounded-full bg-brand px-7 text-[15px] font-semibold text-white shadow-[0_10px_30px_-10px_rgba(0,113,227,0.7)] transition-transform hover:bg-brand-hover active:scale-[0.98] disabled:opacity-60">
                  {micState !== "idle" ? <>Finish speaking first</> : clarifying ? <><CircleNotch size={18} className="animate-spin" /> Thinking…</> : streaming ? <><CircleNotch size={18} className="animate-spin" /> Writing…</> : <>{mode === "generate" ? "Create my prompt" : "Improve my prompt"} <ArrowRight size={17} weight="bold" /></>}
                </button>
              </div>
            </div>

            {followups && !streaming && mode === "generate" && (
              <div ref={fuRef} className="scroll-mt-24">
                <FollowUps questions={followups} onSubmit={answerFollowups} onSkip={() => runGenerate(input)} onCancel={() => setFollowups(null)} />
              </div>
            )}

            {showOutput && (
              <div ref={outRef} className="mt-10 scroll-mt-24 space-y-6" data-testid="output-panel">
                <OutputTerminal title={`Prompt for ${targetLabel}`} text={displayText} streaming={streaming} loading={streaming} target={targetTool} onShare={result?.id ? () => doShare(result) : undefined} onSave={result && !streaming ? () => doSave(result) : undefined} />
                {result && !streaming && <MetricsPanel result={result} />}
              </div>
            )}
          </motion.section>
        )}
      </main>
      <MobileTabBar onSpeak={() => { setMode("generate"); window.scrollTo({ top: 0, behavior: "smooth" }); }} onTemplates={() => setTplOpen(true)} onLibrary={() => setLibOpen(true)} active={tplOpen ? "templates" : libOpen ? "library" : "speak"} />
      <TemplatesGallery open={tplOpen} onOpenChange={setTplOpen} tools={config.target_tools || []} onUse={useTemplate} />
      <LibraryDrawer open={libOpen} onOpenChange={setLibOpen} onSelect={loadLibraryItem} refreshKey={libRefresh} />
    </div>
  );
}
